/**
 * `<ShortfallOptionsModal/>` — the shortfall call-back ladder (Build B).
 *
 * A ward is short of its floor for one session. KKH's practice, in order:
 *   (0) seat someone who is simply free;
 *   (a) call back someone who is post-call;
 *   (b) failing that, cancel someone's leave.
 * All are the rosterer's call, never automatic. The rungs ask differently,
 * because they cost differently: a **recall is one click** ("Recall Rae") —
 * it moves one person's own duty and the ladder exists to be used;
 * **seating a free person asks once more** ("Seat Hana" → "Seat Hana on 55 ·
 * AM"), because it is the lead's own hand placement and the second button
 * names the seat; **cancelling a leave asks twice** ("Confirm" → "Yes,
 * confirm"), because it takes back a day the member was promised. No
 * establishment ceiling, no "just add a person". When a cell has neither
 * rung (a) nor (b), the CELL'S OWN `empty_note` is shown under it —
 * composed from what each rung found on THIS day, and naming it ("Nobody is
 * post-call on Wed 4 Nov and nobody has leave to cancel that day." — never
 * "today", because the ladder opens for any day of the window).
 *
 * **Rung 0 — who is free (founder test, 2 Oct 2026).** Every cell lists the
 * people who could simply be seated — eligible, not away, not on call, not
 * resting post-call, not seated that session — ABOVE the two rungs, most
 * senior grade first, whether or not the rungs have anyone (flows finding
 * 27: a lead bent a hard rest while a free colleague stood unnamed). Each
 * row says what they do that day ("free · on 56 AM that day"). The seat is
 * the grid's hand placement: when the team's scheduling rules object, the
 * row asks again with the rules named ("Seat anyway").
 *
 * **Open the seat to other tiers (#1962, slice 3B of #1947).** When nobody
 * of the seat's own tier is free and no rung has anyone to pick, the cell
 * carries `open_to` and rung 0 reads "Nobody in MO / HO is free. Open this
 * seat to:" with one chip per tier or attribute on the station's Cover from
 * list, and only those (founder decision 1B, 4 Oct 2026: one rule for hand
 * and solver — the team's other grades are no longer offered as dashed
 * suggestions; the seat route refuses anyone the list does not name with
 * `not_on_cover_from`). A chip opens
 * its people as rung-0 rows ("covered here ×2" — #1999: the distinct shifts
 * they have covered there; the not-free reason, button disabled, when they
 * cannot be seated). **Seat <name>** sends `acting_up: true`: the
 * ward-eligibility check is skipped, the seat is pinned (and so counts as
 * their cover while it stands), and their hours stay on their own contract
 * and grade. No `open_to` (or an API before #1962) → nothing.
 *
 * **#1999 (founder, 3 Oct 2026).** A station with an empty Cover from list
 * opens to nobody: the prompt has no chip and says so in one quiet line
 * (`open_to.note`, naming where to set it). A free person the prompt offers
 * is listed under it ONLY (`listed_in_open_to` on their rung-0 row): when
 * nobody of the seat's own grade is free, seating them covers another
 * grade's seat — pinned and counted — so that is the heading that applies,
 * and one person has one button. When rung 0's people moved there, the
 * prompt opens on its first group with anyone free, so nobody free is out
 * of sight.
 *
 * **Why someone is not eligible.** A recall or cancel-leave row the lead
 * cannot pick says why under its tags (`not_eligible_reason`: the ward is
 * not among their eligible stations, and where to change it), and its
 * button is disabled with that sentence as its title and description. A
 * grade note (`tier_note`) says when the ward is also short of a band the
 * person's grade does not fill — said, not enforced.
 *
 * Cells are shown MOST URGENT FIRST: the ward's own coverage priority
 * descending (1-10, 5 the default; higher = more urgent — the same order
 * the coverage heatmap and the solver's H1 penalty read), then AM, PM,
 * full day, on call, then the ward's label. A caption says so, so the lead
 * knows why the first one is first.
 *
 * Candidates who are not eligible for the ward, or already working the
 * session, are collapsed behind a count line rather than listed with a
 * dead Confirm — on a big team they buried the people the lead can
 * actually call.
 *
 * A pooled group floor short across its wards (Build 20, "one registrar
 * somewhere across group A in the PM") is a cell too: titled by its group
 * and grade, one line saying which wards it spans and where a Confirm
 * would seat the person, candidates limited to the floor's grades.
 *
 * Who is listed as post-call follows each on-call type's own rest rule
 * (two rest days list the holder on both; an AM-only rest rests the
 * afternoon and allows a morning seat, so it lists them for the afternoon
 * cell only; a type with no day off lists nobody) — the row
 * says which call, when, and which rest day it is. Someone already called
 * back that day (recalled to the AM, now offered for the PM) says so —
 * "Already called back on 2 Nov · now on 55 AM · no second rest day owed" —
 * because a second session is not a second rest day. After a recall the
 * response may offer a day back: the first following day the person could
 * be released without opening another gap. That offer is shown with its
 * own Confirm ("Give Tue 3 Nov back"); when there is no such day the note
 * says why and nothing is offered.
 *
 * **The owed day outlives the panel.** The offer used to come ONLY from the
 * recall's response, so closing the panel (or reloading) lost it — nothing
 * else in the product offered the day and the recalled person kept working
 * the next morning. The day's GET now serves every pending offer
 * (`rest_offers`), so re-opening the ladder shows the card again — including
 * on a day that reads "Nothing is short", because the recall closed the gap
 * and the day back is all that is left to do. A write's own answer takes
 * precedence over the read behind it, and a settled person drops out at once
 * rather than waiting for the refetch.
 *
 * **Cover asks already out show on the cell.** When a lead has used "Ask
 * the team" (#1603) for a seat, its cell carries "Cover ask out · N" — the
 * words the Review cover inbox uses — so a second lead who opens the day
 * from the MC email (on a phone, never seeing the inbox) does not ask again.
 *
 * **Ask the team from an empty cell (1 Oct 2026).** When neither rung offers
 * anyone the lead can pick and the seat behind the cell belongs to someone
 * off sick that day, the cell ends with one button — "Ask the team to cover
 * 55 · AM" — that sends the same `origin=mc_cover` cover ask Review cover's
 * "Ask the team" sends, narrowed to that seat on that day. Before this the
 * lead had to close the ladder, open Review cover, open the picker and send
 * (two clicks and a detour, flows S3). No second confirmation: an ask writes
 * nothing to the roster. The button turns into "Cover ask out · 1 · the
 * first teammate to accept covers it" in place and the day re-reads. Shown
 * only to a lead (the sick-report read behind it is lead-only), only with
 * both `mc_recover` and `swap_requests` on, and never on a seat the team was
 * already asked about — that cell keeps its "Cover ask out · N".
 *
 * **Who is out sick, at the bottom.** When someone is off sick on this day
 * and their seats are still short, a footer names them with what is still
 * short because of it on THIS day, in the day's own words ("Out sick on
 * 4 Nov: Rae — 56 · AM still short"; a seat filled since reads "covered",
 * one the ladder cannot speak about reads "can't tell", #1999 — so the
 * footer and the day agree). It only names; the rungs above are the
 * remedy. The ladder is the one path for covering a day — the Last-minute
 * cover cascade it used to hand off to ("Reshuffle wards (advanced)") was
 * retired in #1952. The footer's one ghost button, *Fix and re-solve*
 * (#1960), opens the minimal-change re-solve in the right-panel overlay with
 * the absence pinned off (`<FixAndResolvePanel/>`); the ladder closes as it
 * opens. On a day with no uncovered MC the footer is not rendered at all.
 *
 * **A side panel, not a modal (#1922).** On a desktop the ladder is a 480 px
 * non-modal panel at the right edge (`CycleSurfaceModal`'s default): no
 * scrim, the grid behind stays readable, scrollable and clickable, so the
 * lead checks who else is free that day before recalling anyone. It stays
 * open across a scroll and closes on its ✕, `Esc` or the day's chip again
 * (the chip reads pressed while it is open). On the phone roster it is a
 * half-height sheet with no scrim (`snap="half"`), the day card above it.
 *
 * Opened from the roster grid's day header ("Short · options", beside the
 * existing `−N` badge) via `<RosterHeatmap/>`. Reads
 * `GET /api/cycles/{id}/shortfalls?date=…`; writes through the three rung
 * endpoints (seat, recall, cancel-leave), which return the SAME cell re-read after the write (or null
 * once the gap is closed) so the panel can update in place while the
 * roster grid refetches behind it.
 */

import { type RefObject, useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";

import {
  type StandbyCandidate,
  activationNotice,
  gapCallLines,
  keepLabel,
  restDayLabel,
  useActivateStandby,
  useKeepRest,
} from "../../api/useStandbyActivation";

import { ApiError } from "../../api/client";
import {
  type McAttentionAffectedAssignment,
  type McAttentionItem,
  type McCoverAskSkipped,
  useMcAttention,
  useMcCoverBroadcast,
} from "../../api/useMcAttention";
import {
  type CancelLeaveCandidate,
  type FreeCandidate,
  type OpenToGroup,
  type OpenToPerson,
  type RecallCandidate,
  type RestOffer,
  type ShortfallCell,
  type ShortfallMoveCandidate,
  useConfirmCancelLeave,
  useConfirmRecall,
  useConfirmRest,
  useConfirmSeat,
  useMoveHere,
  useShortfallOptions,
} from "../../api/useShortfallOptions";
import { useTeamFeature } from "../../api/useTeamFeature";
import { cn } from "../../lib/cn";
import { isHeldResolveRefusal } from "../../lib/heldResolve";
import { HeldResolveDiscard } from "../HeldResolveDiscard";
import { FixAndResolveButton } from "../repair/FixAndResolveButton";
import { useSeatKnockOnLauncher } from "../review/useSeatKnockOnLauncher";
import { Button } from "../ui";
import type { BottomSheetSnap } from "../ui/BottomSheet";
import { CycleSurfaceModal } from "./CycleSurfaceModal";
import { seatLabel } from "./McAttentionChip";
import { MoveHereRung, moveLine } from "./MoveHereRung";
import { cellRoleTokens } from "./coverAskSeats";
import { standbyErrorText } from "./standbyErrors";

export interface ShortfallOptionsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  cycleId: string;
  /** This roster's team. Only used to point the rest-day notice's "recorded as
   *  leave" link at the live leave list (the Team page's "Leave, call-blocks
   *  & session preferences" section, #730). Omitted → no link, never a dead
   *  one. Backlog #137. */
  teamId?: string | undefined;
  /** ISO date of the day header the lead clicked; null while closed. */
  date: string | null;
  /** Fired after a successful write so the parent can fan out its refetch. */
  onWritten?: () => void;
  /** Phone sheet height (#1922): the phone roster passes `"half"` so the
   *  day card above stays visible while the lead picks. Desktop ignores it
   *  (the ladder is a side panel there). */
  snap?: BottomSheetSnap | undefined;
}

/** `Ward 56 · PM` — the cell's identity, and the panel's react key. A
 *  pooled-floor cell keys on its group, not on the ward it would place on,
 *  so it never collides with that ward's own row. */
function cellKey(cell: {
  station: string;
  session: string;
  kind?: "station" | "floor";
  group?: string | null;
}): string {
  if (cell.kind === "floor") return `floor|${cell.group ?? ""}|${cell.session}`;
  return `${cell.station}|${cell.session}`;
}

/** Does this MC item touch `day`? Prefers the dates of the member's
 *  PUBLISHED rows (the seats that actually emptied) and falls back to the
 *  MC window when the backend didn't serve them. */
function mcTouchesDay(item: McAttentionItem, day: string): boolean {
  const rows = item.affected_assignments ?? [];
  if (rows.length > 0) return rows.some((a) => a.date === day);
  return item.dates.includes(day);
}

/** `Rae Reg — 56 · AM still short` — what is short on THIS day because of
 *  the absence (#1999). Read off the day's own cells (the panel's live
 *  ones, so a seat filled here drops out at once): each of the member's
 *  seats that day is still short when one of the day's cells holds its
 *  role — named as that cell is headed above ("Short by 1, 56 · AM") —
 *  covered when none does, and "can't tell" when the ladder cannot speak
 *  about its role (an on-call the config does not define). The item's own
 *  `still_short` labels carry no date, so on a two-day MC they named the
 *  other day's gap here. Just the name when the read has no seats (an API
 *  before #1603). */
function mcLine(item: McAttentionItem, day: string, cells: ShortfallCell[]): string {
  const seats = (item.affected_assignments ?? []).filter((a) => a.date === day);
  if (seats.length === 0) return item.display_name;
  const short: string[] = [];
  const unknown: string[] = [];
  const covered: string[] = [];
  const add = (list: string[], label: string) => {
    if (!list.includes(label)) list.push(label);
  };
  for (const seat of seats) {
    if (seat.knowable === false) {
      add(unknown, seatLabel(seat.role));
      continue;
    }
    const gap = cells.find((c) => cellRoleTokens(c).has(seat.role));
    if (gap) add(short, cellTitle(gap));
    else add(covered, seatLabel(seat.role));
  }
  const parts: string[] = [];
  if (short.length > 0) parts.push(`${short.join(", ")} still short`);
  if (unknown.length > 0) {
    const verb = unknown.length === 1 ? "is" : "are";
    parts.push(`can't tell whether ${unknown.join(", ")} ${verb} covered`);
  }
  if (covered.length > 0) parts.push(`${covered.join(", ")} covered`);
  return `${item.display_name} — ${parts.join("; ")}`;
}

/** One sick member's seats behind a ladder cell, on the ladder's day. */
interface AskGroup {
  item: McAttentionItem;
  seats: McAttentionAffectedAssignment[];
}

/** The seats behind `cell` that someone off sick on `day` vacated: per MC
 *  item, its published rows that day whose role is one of the cell's. The
 *  join is the roster's own role grammar (`cellRoleTokens`), never a parse
 *  of the label. */
function askGroupsFor(cell: ShortfallCell, items: McAttentionItem[], day: string): AskGroup[] {
  const tokens = cellRoleTokens(cell);
  return items
    .map((item) => ({
      item,
      seats: (item.affected_assignments ?? []).filter((a) => a.date === day && tokens.has(a.role)),
    }))
    .filter((group) => group.seats.length > 0);
}

/** `55 · AM` — the seat the ask is for. A ward's cell names itself; a
 *  pooled floor names the sick member's own seat behind it. */
function askSeatWords(cell: ShortfallCell, groups: AskGroup[]): string {
  if (cell.kind !== "floor") return `${cell.station} · ${sessionLabel(cell.session)}`;
  const words = groups.flatMap((group) => group.seats.map((seat) => seatLabel(seat.role)));
  return Array.from(new Set(words)).join(", ");
}

/** Why a click sent no ask — the broadcast's skip codes, in words. */
function askNoneText(skipped: McCoverAskSkipped[]): string {
  const reasons = new Set(skipped.map((s) => s.reason));
  if (reasons.has("swap_already_open_for_assignment")) {
    return "No cover ask was sent — a swap is already open on this seat.";
  }
  if (reasons.has("swap_past_shift")) return "No cover ask was sent — that day has passed.";
  return "No cover ask was sent — reopen the options to see the seat as it stands.";
}

/** What the last "Ask the team" click on a cell came back with. */
interface AskOutcome {
  created: number;
  skipped: McCoverAskSkipped[];
}

/** The cell as the lead would say it: `Ward 56 · PM`, or for a pooled
 *  floor `Group A pooled REG · PM`. */
function cellTitle(cell: ShortfallCell): string {
  if (cell.kind === "floor") {
    return `Group ${cell.group ?? ""} pooled ${(cell.floor_label ?? "").toUpperCase()} · ${
      cell.session
    }`;
  }
  return `${cell.station} · ${cell.session}`;
}

/** Where a Confirm on this cell seats the person, in the question's words. */
function placeText(cell: ShortfallCell): string {
  return `${cell.station} ${sessionLabel(cell.session)}`;
}

/** `22 Sep` — the short date the confirm line reads with. */
function shortDate(iso: string | null): string {
  if (!iso) return "";
  const parts = iso.split("-");
  if (parts.length !== 3) return iso;
  const [y, m, d] = parts.map((s) => Number.parseInt(s, 10));
  if (!y || !m || !d) return iso;
  const dt = new Date(Date.UTC(y, m - 1, d));
  return `${d} ${dt.toLocaleString("en-GB", { month: "short", timeZone: "UTC" })}`;
}

/** `AM` / `PM` / `full day` / `on call` — the session in the lead's words. */
function sessionLabel(session: string): string {
  const s = session.toUpperCase();
  if (s === "FULL_DAY") return "full day";
  if (s === "ONCALL") return "on call";
  return s;
}

/** `post-call of NIGHT · on call Mon 2 Nov · rest day 1 of 2 · may work the
 *  morning` — the recall row's factual middle line. An AM-only rest allows
 *  the morning seat and rests the afternoon (the solver's H5b). */
function recallLine(candidate: RecallCandidate): string {
  const parts = [
    `post-call of ${candidate.post_call_of}`,
    `on call ${shortDate(candidate.held_on)}`,
  ];
  if (candidate.rest_days > 1)
    parts.push(`rest day ${candidate.rest_day} of ${candidate.rest_days}`);
  if (candidate.rest_am_only) parts.push("may work the morning");
  return parts.join(" · ");
}

/** Lifecycle words the backend's `approved_by` sometimes carries instead of a
 *  person: a status, or the fact that nobody approved it by hand. Rendering
 *  one as "approved by confirmed" reads like a name and is not one. */
const LIFECYCLE_WORDS = new Set([
  "approved",
  "auto",
  "confirmed",
  "declined",
  "import",
  "imported",
  "indicated",
  "lead",
  "member",
  "planned",
  "superseded",
  "system",
  "under_review",
  "withdrawn",
]);

/** `under_review` → `under review`. The lead's words, not the column's. */
function stateWord(value: string): string {
  return value.trim().replace(/_/g, " ");
}

/** `leave · 1 Nov · confirmed` — the cancel-leave row's factual middle line:
 *  what kind of leave, when it runs, and where it stands.
 *
 *  A one-day leave reads as one date, not `1 Nov → 1 Nov`. `status` is shown
 *  because cancelling a leave that is only under review is a different act
 *  from cancelling a confirmed one, and the lead must be able to tell before
 *  they answer the Confirm. `approved_by` is only rendered as an approver
 *  when it actually names one. */
function cancelLeaveLine(candidate: CancelLeaveCandidate): string {
  const when =
    candidate.start_date === candidate.end_date
      ? shortDate(candidate.start_date)
      : `${shortDate(candidate.start_date)} → ${shortDate(candidate.end_date)}`;
  const parts = [candidate.leave_type, when];
  const status = stateWord(candidate.status ?? "");
  const approver = (candidate.approved_by ?? "").trim();
  const approverIsWord = LIFECYCLE_WORDS.has(approver.toLowerCase());
  if (status) parts.push(status);
  if (approver && !approverIsWord && approver.toLowerCase() !== status.toLowerCase()) {
    parts.push(`approved by ${approver}`);
  } else if (approver && approverIsWord && !status) {
    parts.push(stateWord(approver));
  }
  return parts.join(" · ");
}

/** `Rae` — the recall button says the person's first name, because the
 *  button IS the write now: "Recall Rae", not "Confirm". */
function firstName(displayName: string): string {
  return displayName.trim().split(/\s+/)[0] || displayName;
}

/** What the lead is told after a recall: where the person is now, WHAT they
 *  are being told, and what the department owes them for it.
 *
 *  "Rae Reg is placed on 55 AM · they will be told" read like an ordinary
 *  placement. A recall takes someone off rest — the message they get says
 *  so, and the rest days owed are the other half of the act. */
export function recallNotice(
  displayName: string,
  cell: ShortfallCell,
  daysOwed?: number | null,
  calledBackTo?: string[] | null,
): string {
  // A second session the same day: the rest was already broken by the first
  // recall, so this one owes nothing new — say that, not "1 rest day owed"
  // again, which read as a second debt.
  if (calledBackTo && calledBackTo.length > 0) {
    return [
      `${displayName} is placed on ${placeText(cell)} too`,
      `already on ${calledBackTo.join(", ")} that day`,
      "they will be told",
      "no second rest day owed",
    ].join(" · ");
  }
  const owed = typeof daysOwed === "number" && daysOwed > 0 ? daysOwed : null;
  return [
    `${displayName} is placed on ${placeText(cell)}`,
    "they will be told",
    ...(owed ? [`${owed} rest day${owed === 1 ? "" : "s"} owed`] : []),
  ].join(" · ");
}

/** AM before PM before the full day before the on-call: the order a day is
 *  worked, used to break ties between two wards of the same priority. */
const SESSION_RANK = ["AM", "PM", "FULL_DAY", "ONCALL"];

function sessionRank(session: string): number {
  const i = SESSION_RANK.indexOf(session.toUpperCase());
  return i < 0 ? SESSION_RANK.length : i;
}

/** Most urgent first. `priority` is the ward's own coverage priority
 *  (1-10, default 5) and HIGHER is more urgent — the same reading as the
 *  coverage heatmap ("sorted by station priority, highest first") and the
 *  solver's H1 shortfall weight. A pooled-floor cell carries the highest
 *  priority of the wards its group spans. Ties: session order, then the
 *  ward's label so the list never shuffles between reads. */
function orderCells(cells: ShortfallCell[]): ShortfallCell[] {
  return [...cells].sort(
    (a, b) =>
      b.priority - a.priority ||
      sessionRank(a.session) - sessionRank(b.session) ||
      a.station.localeCompare(b.station),
  );
}

/** Why a candidate is listed but cannot be picked, or null when they can. */
function blockedReason(candidate: {
  eligible: boolean;
  already_working?: boolean;
}): "not_eligible" | "already_working" | null {
  if (!candidate.eligible) return "not_eligible";
  if (candidate.already_working) return "already_working";
  return null;
}

/** The sentence a disabled row's button carries as its title and
 *  description — why it cannot be picked. */
function blockedText(candidate: {
  eligible: boolean;
  already_working?: boolean;
  not_eligible_reason?: string | null;
  display_name: string;
}): string | undefined {
  if (!candidate.eligible) {
    return candidate.not_eligible_reason || `${candidate.display_name} is not listed as eligible`;
  }
  if (candidate.already_working) {
    return `${candidate.display_name} is already working this session`;
  }
  return undefined;
}

/** `3 more not shown (2 not eligible here, 1 already working)` — the count
 *  line over the collapsed candidates. */
function hiddenCountLine(notEligible: number, alreadyWorking: number): string {
  const parts: string[] = [];
  if (notEligible) parts.push(`${notEligible} not eligible here`);
  if (alreadyWorking) parts.push(`${alreadyWorking} already working`);
  const total = notEligible + alreadyWorking;
  return `${total} more not shown (${parts.join(", ")})`;
}

/** The one pending question. Only ever one open at a time. */
interface PendingConfirm {
  key: string;
  cellKey: string;
  question: string;
  /** The confirm button's words; `Yes, confirm` when omitted. */
  confirmLabel?: string;
  /** Resolves to a FOLLOW-UP question to park in its place (the seat's
   *  "Seat anyway" after the scheduling rules object), or nothing. */
  run: () => Promise<PendingConfirm | undefined>;
}

/** The scheduling rules a hand placement goes against, from the grid's
 *  #1179 refusal (422, `{code, message, <list of {rule, message}>}`), or
 *  null when the error is anything else. Read by shape, not by the code's
 *  words, so the ladder can ask "Seat anyway?" with the rules named. */
export function rulesObjecting(err: unknown): string[] | null {
  if (!(err instanceof ApiError) || err.status !== 422) return null;
  const body = err.body;
  if (!body || typeof body !== "object") return null;
  const errorBody = (body as { error?: unknown }).error;
  if (!errorBody || typeof errorBody !== "object") return null;
  const detail = (errorBody as { detail?: unknown }).detail;
  if (!detail || typeof detail !== "object" || Array.isArray(detail)) return null;
  const list = Object.values(detail as Record<string, unknown>).find(Array.isArray);
  if (!Array.isArray(list)) return null;
  const messages = list
    .map((item) =>
      item && typeof item === "object" ? (item as { message?: unknown }).message : null,
    )
    .filter((m): m is string => typeof m === "string" && m.length > 0);
  return Array.from(new Set(messages));
}

/** Rung 0 shows this many free people before a "N more free · Show all"
 *  line: on a big team a quiet afternoon has twenty free, and the list must
 *  not bury the two rungs under it. The most senior come first (the API's
 *  order). */
const FREE_SHOWN = 4;

/** `free · on 56 AM that day` — what a free person does that day. */
function freeLine(candidate: FreeCandidate): string {
  return candidate.doing || "free";
}

/** #1962 — one chip of the "Open this seat to" prompt, keyed within its cell. */
/** #2211 — where the standby is that night, and what their rest costs, in
 *  the team's own setting: `whole_day` (default) — *Fri 16 Oct off: 56 AM,
 *  56 PM come off as part of this*; `ask` — *… come off once you confirm the
 *  rest*. */
export function standbyLine(candidate: StandbyCandidate, mode?: string | null): string {
  const where = `on ${candidate.standby_label} that night`;
  const day = candidate.rest_date ? restDayLabel(candidate.rest_date) : null;
  const seats = candidate.rest_day_seats ?? [];
  if (!day) return where;
  if (seats.length === 0) return `${where} · nothing to release on ${day}`;
  return mode === "ask"
    ? `${where} · ${day}: ${seats.join(", ")} come off once you confirm the rest`
    : `${where} · ${day} off: ${seats.join(", ")} come off as part of this`;
}

function openGroupKey(group: OpenToGroup): string {
  return `${group.kind}:${group.key}`;
}

/** `free · covered here ×2` — an opened-seat row's middle line. */
function openLine(person: OpenToPerson): string {
  const parts = [person.not_free_reason ? "not free" : "free"];
  if ((person.covered_here ?? 0) > 0) parts.push(`covered here ×${person.covered_here}`);
  return parts.join(" · ");
}

/** The lines under a row's tags that say why it reads as it does. */
function whyLines(candidate: {
  not_eligible_reason?: string | null;
  tier_note?: string | null;
}): string[] {
  return [candidate.not_eligible_reason, candidate.tier_note].filter(
    (line): line is string => typeof line === "string" && line.length > 0,
  );
}

/**
 * Backlog #92 (checklist 06.19) — the ladder's 409s are CODES, not copy.
 *
 * Every one of them means the same human thing: the day moved under the
 * lead between the read and the click (another cell's rung took the leave,
 * a swap filled the seat, the person picked up work). Rendering
 * `not_cancellable:declined` verbatim told the lead nothing and named a
 * state ("declined") the ladder never shows. Each code gets a sentence that
 * says what happened and what to do — reopen the options.
 *
 * Unknown codes fall through to the raw detail: a new backend code must not
 * be silently swallowed into a generic sentence.
 */
const REOPEN = "reopen the options to see who is still available.";

export function shortfallErrorText(err: unknown): string {
  if (!(err instanceof ApiError)) {
    if (err instanceof Error) return err.message;
    return "That could not be saved. Please try again.";
  }
  const detail = err.detail;
  // `not_cancellable:<leave status>` — the leave is no longer confirmed, so
  // there is nothing to cancel. Almost always: another cell just took it.
  if (detail.startsWith("not_cancellable")) {
    return `This leave can no longer be cancelled — ${REOPEN}`;
  }
  // Founder decision: an MC is never declined. The backend already appends
  // the sentence; show that, not the code in front of it.
  if (detail.startsWith("mc_not_declinable")) {
    const tail = detail.slice("mc_not_declinable:".length).trim();
    return tail || "An MC is recorded, not reviewed, so it cannot be cancelled.";
  }
  if (detail.startsWith("cell_not_short")) {
    return "That seat is no longer short — someone else has already covered it.";
  }
  if (detail.startsWith("candidate_not_on_rung")) {
    return `That person is no longer on this rung — ${REOPEN}`;
  }
  // `candidate_not_free: Mo Ong is already seated on 56 AM on Mon 2 Nov` —
  // rung 0's refusal carries its own sentence: keep it, drop the code.
  if (detail.startsWith("candidate_not_free")) {
    const tail = detail.slice("candidate_not_free:".length).trim();
    return tail ? `${tail} — ${REOPEN}` : `That person is no longer free for this seat — ${REOPEN}`;
  }
  if (detail.startsWith("candidate_not_eligible")) {
    const tail = detail.slice("candidate_not_eligible:".length).trim();
    return tail || `That person is no longer eligible for this ward — ${REOPEN}`;
  }
  if (detail.startsWith("candidate_already_working")) {
    return `That person is already working this session — ${REOPEN}`;
  }
  // #1999 — the ward's Cover from list was emptied after the panel opened:
  // nobody may act up into it. The backend's sentence says where to set it.
  if (detail.startsWith("cover_from_empty")) {
    const tail = detail.slice("cover_from_empty:".length).trim();
    return tail || "Nobody of another grade may act up into this ward.";
  }
  // 1B — the ward's Cover from list no longer names this person's grade (it
  // changed after the panel opened). The backend's sentence says where to add it.
  if (detail.startsWith("not_on_cover_from")) {
    const tail = detail.slice("not_on_cover_from:".length).trim();
    return tail || "That person's grade is not on this ward's Cover from list.";
  }
  if (detail.startsWith("already_off_that_day")) {
    return "They are already off that day, so there is nothing to give back.";
  }
  if (detail.startsWith("nothing_to_release")) {
    return "There is nothing to release on that day any more.";
  }
  // `not_safe: releasing them would leave 55 AM short` — already a sentence
  // behind the code; keep the sentence, drop the code.
  if (detail.startsWith("not_safe:")) {
    const tail = detail.slice("not_safe:".length).trim();
    return tail ? `That day can't be given back: ${tail}` : "That day can't be given back.";
  }
  return standbyErrorText(detail) ?? detail;
}

export function ShortfallOptionsModal({
  open,
  onOpenChange,
  cycleId,
  teamId,
  date,
  onWritten,
  snap,
}: ShortfallOptionsModalProps) {
  const optionsQuery = useShortfallOptions(cycleId, date, { enabled: open });
  // #2211 — the team's next-day setting words the standby rung and its question.
  const standbyRestMode = optionsQuery.data?.standby_rest_mode ?? "whole_day";
  // The footer's read: who is off sick on THIS day with seats still short.
  // Lead-only + `mc_recover`-gated server-side — a 404 or an empty list
  // simply means no footer (`retry: false`, so no noise).
  const attentionQuery = useMcAttention(cycleId, Boolean(open && date));
  const recall = useConfirmRecall(cycleId);
  const cancelLeave = useConfirmCancelLeave(cycleId);
  const rest = useConfirmRest(cycleId);
  const seat = useConfirmSeat(cycleId);
  // #2412 — Move here: someone seated on another ward moves onto the short cell.
  const moveHere = useMoveHere(cycleId);
  // #2211 — a short real call: activate someone on standby that night.
  const activate = useActivateStandby(cycleId);
  // #2211 (founder, 12 Oct) — keep someone on a short post-call day.
  const keep = useKeepRest(cycleId);
  // #1964 — a person who sits on another ward that session: preview the move.
  const knockOn = useSeatKnockOnLauncher(cycleId);
  // "Ask the team" from an empty cell: the cover ask rides the swap rows, so
  // it needs both components (the broadcast refuses without `swap_requests`).
  // Unknown (loading, or no team id) reads as off — no button.
  const mcRecoverOn = useTeamFeature(teamId, "mc_recover") === true;
  const swapsOn = useTeamFeature(teamId, "swap_requests") === true;
  const canAskTeam = mcRecoverOn && swapsOn;
  const broadcast = useMcCoverBroadcast(cycleId);

  // #1934 — where focus goes when the panel closes and its opener is gone:
  // the email link (`?date=…&ladder=1`) opens it with focus on <body>, and
  // the Review cover card's row unmounts as it opens it. The day's own
  // "Short · options" chip in the grid, else the Review cover chip (the
  // main landmark is `SideDrawer`'s last resort). Resolved at close time,
  // when `date` is already null, so the last day shown is kept.
  const lastDateRef = useRef<string | null>(date);
  if (date) lastDateRef.current = date;
  const fallbackFocusRef = useMemo<RefObject<HTMLElement | null>>(
    () => ({
      get current() {
        const day = lastDateRef.current;
        const dayChip = day
          ? document.querySelector<HTMLElement>(`[data-testid="shortfall-options-chip-${day}"]`)
          : null;
        return dayChip ?? document.querySelector<HTMLElement>('[data-testid="mc-attention-chip"]');
      },
    }),
    [],
  );

  // The panel's live view of the day's short cells. Seeded from the fetch,
  // then updated in place from each write's re-read `cell` (or the row is
  // dropped when the response's `cell` is null — the gap is closed).
  const [cells, setCells] = useState<ShortfallCell[]>([]);
  const [pending, setPending] = useState<PendingConfirm | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // The rest write records an ordinary `leave` row; the notice points at it.
  const [noticeLeaveId, setNoticeLeaveId] = useState<string | null>(null);
  const [writeError, setWriteError] = useState<string | null>(null);
  // The one-step recall has no parked question, so the row itself has to say
  // it is writing: this is the row key that is in flight.
  const [savingKey, setSavingKey] = useState<string | null>(null);
  // Cells whose blocked candidates the lead has asked to see, by cell key.
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  // Cells whose whole free list (rung 0) the lead has asked to see.
  const [freeExpanded, setFreeExpanded] = useState<Record<string, boolean>>({});
  // Cells whose whole Move here list the lead has asked to see.
  const [moveExpanded, setMoveExpanded] = useState<Record<string, boolean>>({});
  // #1962 — per cell key: the "Open this seat to" chip that is open, and
  // whether its whole list is shown.
  const [openChip, setOpenChip] = useState<Record<string, string | null>>({});
  const [openAll, setOpenAll] = useState<Record<string, boolean>>({});
  // The last WRITE's rest offer (or the reason there is none). The day's GET
  // serves the pending ones; this is the one the lead's own click just
  // produced, which is newer than the read behind it.
  const [restOffer, setRestOffer] = useState<RestOffer | null>(null);
  // People whose owed rest was settled in this panel. The GET behind the
  // write still lists them until it refetches, and a card that re-appears
  // for a second after the lead gave the day back reads as a failed write.
  const [restSettled, setRestSettled] = useState<string[]>([]);
  // "Ask the team" per cell key: what the click came back with, which cell
  // is asking right now, and the cells whose ask failed (the button stays).
  const [askOutcomes, setAskOutcomes] = useState<Record<string, AskOutcome>>({});
  const [askingKey, setAskingKey] = useState<string | null>(null);
  const [askFailed, setAskFailed] = useState<string[]>([]);

  useEffect(() => {
    setCells(optionsQuery.data?.cells ?? []);
  }, [optionsQuery.data]);

  // A fresh day (or a re-open) starts with no parked question and no notice.
  // biome-ignore lint/correctness/useExhaustiveDependencies: date + open are the explicit reset triggers, not values read in the body
  useEffect(() => {
    setPending(null);
    setNotice(null);
    setNoticeLeaveId(null);
    setWriteError(null);
    setRestOffer(null);
    setRestSettled([]);
    setSavingKey(null);
    setExpanded({});
    setFreeExpanded({});
    setMoveExpanded({});
    setOpenChip({});
    setOpenAll({});
    setAskOutcomes({});
    setAskingKey(null);
    setAskFailed([]);
  }, [date, open]);

  const applyWrite = useCallback(
    (key: string, nextCell: ShortfallCell | null) => {
      setCells((prev) => {
        if (!nextCell) return prev.filter((c) => cellKey(c) !== key);
        return prev.map((c) => (cellKey(c) === key ? nextCell : c));
      });
      onWritten?.();
    },
    [onWritten],
  );

  // Rung (a) writes on ONE click ("Recall Rae"): a recall moves one
  // person's own duty and the ladder exists to be used — the second click
  // was friction, not a safeguard. The post-write notice (and the rest
  // offer under it) is what tells the lead it happened; rung (b), which
  // takes back a promised day off, still asks twice.
  const doRecall = useCallback(
    async (cell: ShortfallCell, candidate: RecallCandidate) => {
      if (!date) return;
      const key = cellKey(cell);
      const rowKey = `recall:${key}:${candidate.membership_id}`;
      setWriteError(null);
      setNotice(null);
      setNoticeLeaveId(null);
      setPending(null);
      setSavingKey(rowKey);
      try {
        const res = await recall.mutateAsync({
          date,
          station: cell.station,
          session: cell.session,
          membershipId: candidate.membership_id,
          floorGroup: cell.kind === "floor" ? (cell.group ?? null) : null,
        });
        applyWrite(key, res.cell);
        setRestOffer(res.rest_offer ?? null);
        // A recall is not an ordinary placement and the notice must not read
        // like one: the person was resting, they are being brought back, and
        // the department owes them the day. Saying only "they will be told"
        // left the lead to remember the debt unaided.
        setNotice(
          recallNotice(
            candidate.display_name,
            cell,
            res.rest_offer?.days_owed,
            candidate.called_back_to,
          ),
        );
      } catch (err) {
        setWriteError(shortfallErrorText(err));
      } finally {
        setSavingKey(null);
      }
    },
    [applyWrite, date, recall],
  );

  const askRest = useCallback(
    (offer: RestOffer) => {
      if (!offer.date) return;
      const day = offer.date;
      // #2211 — the post-call day of a call someone was activated onto: the
      // day after the call itself, released even when a seat goes short.
      // A day back later after a keep (`call_later`) waits for a safe day.
      const afterCall = offer.kind === "call";
      const callKind = offer.kind === "call" || offer.kind === "call_later";
      setWriteError(null);
      setPending({
        key: `rest:${offer.membership_id}:${day}`,
        cellKey: "rest",
        question: `Release ${offer.display_name} from ${(offer.released ?? []).join(", ")} on ${
          callKind ? restDayLabel(day) : shortDate(day)
        } and record it as rest?${afterCall && !offer.safe ? " A seat goes short that day." : ""}`,
        run: async () => {
          const res = await rest.mutateAsync({
            membershipId: offer.membership_id,
            recallDate: offer.recall_date,
            date: day,
            daysOwed: offer.days_owed ?? 1,
            ...(callKind
              ? {
                  kind: offer.kind === "call_later" ? ("call_later" as const) : ("call" as const),
                  restCycleId: offer.cycle_id ?? null,
                }
              : {}),
          });
          // A two-day rest owes two days: the write answers with the next
          // day's offer, so the card re-shows instead of the lead having to
          // re-open the ladder. One day owed → `next_offer` is null, the
          // debt is settled, and the card closes (the day's GET still lists
          // it until the refetch behind this write lands, so say so here).
          setRestOffer(res.next_offer ?? null);
          if (!res.next_offer) {
            setRestSettled((prev) =>
              prev.includes(offer.membership_id) ? prev : [...prev, offer.membership_id],
            );
          }
          setNoticeLeaveId(res.leave_id ?? null);
          setNotice(
            `${offer.display_name} is released from ${res.released.join(", ")} on ${
              callKind ? restDayLabel(day) : shortDate(day)
            } · recorded as rest · they will be told`,
          );
          onWritten?.();
          return undefined;
        },
      });
    },
    [onWritten, rest],
  );

  // Rung 0: "Seat Hana" parks one question whose button names the seat
  // ("Seat Hana on 55 · AM"). If the team's scheduling rules object (the
  // grid's #1179 check), the row asks again with the rules named and
  // "Seat anyway" re-sends with the acknowledgement. `actingUp` (#1962): a
  // seat the lead opened to another tier — its own row key, and the write
  // says so (the ward-eligibility check skipped, the cover counted).
  const askSeat = useCallback(
    (
      cell: ShortfallCell,
      candidate: { membership_id: string; display_name: string },
      actingUp = false,
    ) => {
      const key = cellKey(cell);
      const rowKey = `${actingUp ? "seat-open" : "seat"}:${key}:${candidate.membership_id}`;
      const first = firstName(candidate.display_name);
      const seatWords = `${cell.station} · ${sessionLabel(cell.session)}`;
      setWriteError(null);
      setNotice(null);
      setNoticeLeaveId(null);
      setRestOffer(null);
      const write =
        (acknowledgeRules: boolean) => async (): Promise<PendingConfirm | undefined> => {
          if (!date) return undefined;
          try {
            const res = await seat.mutateAsync({
              date,
              station: cell.station,
              session: cell.session,
              membershipId: candidate.membership_id,
              floorGroup: cell.kind === "floor" ? (cell.group ?? null) : null,
              acknowledgeRules,
              ...(actingUp ? { actingUp: true } : {}),
            });
            applyWrite(key, res.cell);
            setNotice(
              `${candidate.display_name} is placed on ${placeText(cell)} · they will be told${
                actingUp ? " · added to their cover history" : ""
              }`,
            );
            return undefined;
          } catch (err) {
            const rules = acknowledgeRules ? null : rulesObjecting(err);
            if (!rules) throw err;
            return {
              key: rowKey,
              cellKey: key,
              question: `This goes against the team's rules: ${
                rules.length > 0 ? rules.join(" ") : "see the roster check"
              } Seat ${first} anyway?`,
              confirmLabel: "Seat anyway",
              run: write(true),
            };
          }
        };
      setPending({
        key: rowKey,
        cellKey: key,
        question: `Seat ${candidate.display_name} on ${seatWords}, ${shortDate(date)}?`,
        confirmLabel: `Seat ${first} on ${seatWords}`,
        run: write(false),
      });
    },
    [applyWrite, date, seat],
  );

  // #2412 — Move here: "Move Dr Tan to 64A · PM" asks once more, then writes
  // `POST …/shortfalls/move`. The move waits for Publish, so the notice says
  // members hear when the lead publishes. If the team's scheduling rules
  // object, the row asks again with the rules named ("Move anyway").
  const askMove = useCallback(
    (cell: ShortfallCell, candidate: ShortfallMoveCandidate) => {
      const key = cellKey(cell);
      const rowKey = `move:${key}:${candidate.membership_id}`;
      const seatWords = `${cell.station} · ${sessionLabel(cell.session)}`;
      setWriteError(null);
      setNotice(null);
      setNoticeLeaveId(null);
      setRestOffer(null);
      const write =
        (acknowledgeRules: boolean) => async (): Promise<PendingConfirm | undefined> => {
          if (!date) return undefined;
          try {
            const res = await moveHere.mutateAsync({
              date,
              station: cell.station,
              session: cell.session,
              membershipId: candidate.membership_id,
              floorGroup: cell.kind === "floor" ? (cell.group ?? null) : null,
              acknowledgeRules,
            });
            applyWrite(key, res.cell);
            setNotice("Moved. It reaches members when you publish.");
            return undefined;
          } catch (err) {
            const rules = acknowledgeRules ? null : rulesObjecting(err);
            if (!rules) throw err;
            return {
              key: rowKey,
              cellKey: key,
              question: `This goes against the team's rules: ${
                rules.length > 0 ? rules.join(" ") : "see the roster check"
              } Move ${candidate.display_name} anyway?`,
              confirmLabel: "Move anyway",
              run: write(true),
            };
          }
        };
      setPending({
        key: rowKey,
        cellKey: key,
        question: `Move ${candidate.display_name} from ${candidate.from_station} to ${seatWords}, ${shortDate(
          date,
        )}? Members keep the old seat until you publish.`,
        confirmLabel: `Move ${candidate.display_name} to ${seatWords}`,
        run: write(false),
      });
    },
    [applyWrite, date, moveHere],
  );

  // #2211 (founder, 12 Oct) — the team asks each time and the post-call day
  // is short: keep them on it. One click (nothing on the roster moves); the
  // answer is the recall rule's day back later, or why the rest was not given.
  const doKeep = useCallback(
    async (offer: RestOffer) => {
      if (!offer.date) return;
      setWriteError(null);
      setPending(null);
      try {
        const res = await keep.mutateAsync({
          membershipId: offer.membership_id,
          callDate: offer.recall_date,
          date: offer.date,
          restCycleId: offer.cycle_id ?? null,
        });
        setRestOffer(res.offer ?? null);
        setNotice(
          `${offer.display_name} stays on ${(offer.released ?? []).join(", ")} on ${restDayLabel(
            offer.date,
          )} · the rest was not given that day`,
        );
        onWritten?.();
      } catch (err) {
        setWriteError(shortfallErrorText(err));
      }
    },
    [keep, onWritten],
  );

  // #2211 — rung "Activate a standby" on a short real call: one parked
  // question whose button names the move ("Activate Lee on MO1"), because it
  // takes the call off the sick holder and moves the standby's night.
  const askActivate = useCallback(
    (cell: ShortfallCell, candidate: StandbyCandidate) => {
      const key = cellKey(cell);
      const first = firstName(candidate.display_name);
      setWriteError(null);
      setNotice(null);
      setNoticeLeaveId(null);
      setRestOffer(null);
      const restWords = candidate.rest_date
        ? standbyRestMode === "ask"
          ? ` and ${first}'s rest on ${restDayLabel(candidate.rest_date)} is offered next`
          : (candidate.rest_day_seats ?? []).length > 0
            ? ` and ${first} gets ${restDayLabel(candidate.rest_date)} off (${(
                candidate.rest_day_seats ?? []
              ).join(", ")} come off)`
            : ` and ${restDayLabel(candidate.rest_date)} is ${first}'s rest`
        : "";
      setPending({
        key: `standby:${key}:${candidate.membership_id}`,
        cellKey: key,
        question: `Move ${candidate.display_name} from ${candidate.standby_label} to ${
          cell.station
        } on ${shortDate(date)}? The call comes off whoever is away, both are told${restWords}.`,
        confirmLabel: `Activate ${first} on ${cell.station}`,
        run: async () => {
          if (!date) return undefined;
          const res = await activate.mutateAsync({
            date,
            membershipId: candidate.membership_id,
            callPost: cell.station,
          });
          applyWrite(key, res.cell ?? null);
          setRestOffer(res.rest_offer ?? null);
          setNotice(activationNotice(res, candidate.display_name, restDayLabel(date)));
          return undefined;
        },
      });
    },
    [activate, applyWrite, date, standbyRestMode],
  );

  const askCancelLeave = useCallback(
    (cell: ShortfallCell, candidate: CancelLeaveCandidate) => {
      const key = cellKey(cell);
      setWriteError(null);
      setNotice(null);
      setNoticeLeaveId(null);
      // Only the WRITE-local offer is dropped: it belongs to the recall that
      // produced it, and leaving it up would attach it to a cancel-leave that
      // never made one. Any rest the day still OWES comes from the GET and
      // stays — it is the day's debt, not this click's.
      setRestOffer(null);
      setPending({
        key: `cancel:${key}:${candidate.leave_id}`,
        cellKey: key,
        question: `Cancel ${candidate.display_name}'s ${candidate.leave_type} on ${shortDate(
          date,
        )} and place them on ${placeText(cell)}?`,
        run: async () => {
          if (!date) return;
          const res = await cancelLeave.mutateAsync({
            date,
            station: cell.station,
            session: cell.session,
            leaveId: candidate.leave_id,
            floorGroup: cell.kind === "floor" ? (cell.group ?? null) : null,
          });
          applyWrite(key, res.cell);
          setNotice(
            `${candidate.display_name} is placed on ${placeText(cell)} · they will be told`,
          );
          return undefined;
        },
      });
    },
    [applyWrite, cancelLeave, date],
  );

  const confirmPending = useCallback(async () => {
    if (!pending) return;
    try {
      const next = await pending.run();
      setPending(next ?? null);
    } catch (err) {
      setPending(null);
      setWriteError(shortfallErrorText(err));
    }
  }, [pending]);

  // "Ask the team" writes on ONE click: an ask puts nothing on the roster —
  // the first teammate to accept fills the seat — so the "ask once" rule for
  // writes does not apply. One broadcast per sick member (the endpoint asks
  // on one member's behalf), each narrowed to the seat(s) behind THIS cell on
  // THIS day. The hook re-reads the inbox and this day's ladder behind it.
  const askTeam = useCallback(
    async (cell: ShortfallCell, groups: AskGroup[]) => {
      if (!date || groups.length === 0) return;
      const key = cellKey(cell);
      setAskingKey(key);
      setAskFailed((prev) => prev.filter((k) => k !== key));
      try {
        const results = await Promise.all(
          groups.map((group) =>
            broadcast.mutateAsync({
              mc_membership_id: group.item.membership_id,
              dates: [date],
              assignment_ids: group.seats.map((seat) => seat.assignment_id),
            }),
          ),
        );
        setAskOutcomes((prev) => ({
          ...prev,
          [key]: {
            created: results.reduce((n, res) => n + res.created.length, 0),
            skipped: results.flatMap((res) => res.skipped),
          },
        }));
      } catch (err: unknown) {
        // #2263 — a held re-solve's refusal names its way out (the discard
        // button under the panel's error), not "try again".
        if (isHeldResolveRefusal(err)) setWriteError(shortfallErrorText(err));
        else setAskFailed((prev) => (prev.includes(key) ? prev : [...prev, key]));
      } finally {
        setAskingKey(null);
      }
    },
    [broadcast, date],
  );

  const busy =
    recall.isPending ||
    cancelLeave.isPending ||
    rest.isPending ||
    seat.isPending ||
    moveHere.isPending ||
    activate.isPending ||
    keep.isPending;
  const ordered = useMemo(() => orderCells(cells), [cells]);
  const loadError = optionsQuery.error ? shortfallErrorText(optionsQuery.error) : null;
  const emptyNote = optionsQuery.data?.empty_note ?? "";

  // Only the MCs that touch THIS day. The backend already drops an MC
  // whose seats are all covered again, so anything here is still open.
  const mcToday = useMemo(
    () =>
      date ? (attentionQuery.data?.items ?? []).filter((item) => mcTouchesDay(item, date)) : [],
    [attentionQuery.data, date],
  );

  // Every rest this day still owes: the ones the GET serves (so the card
  // survives a close or a reload), with the one the lead's own write just
  // produced in front of its stale twin, and anyone already settled dropped.
  const restOffers = useMemo(() => {
    const served = (optionsQuery.data?.rest_offers ?? []).filter(
      (o) => !restSettled.includes(o.membership_id) && o.membership_id !== restOffer?.membership_id,
    );
    return restOffer ? [restOffer, ...served] : served;
  }, [optionsQuery.data, restOffer, restSettled]);

  return (
    <CycleSurfaceModal
      open={open}
      onOpenChange={onOpenChange}
      title="Shortfall options"
      {...(date
        ? {
            description: shortDate(date),
          }
        : {})}
      testid="shortfall-options-modal"
      // #1922 — a 480 px side panel: the lead reads the grid behind it
      // (who else is free, what the day's column says) before recalling.
      panelWidth="lg"
      snap={snap}
      fallbackFocusRef={fallbackFocusRef}
    >
      <div className="space-y-4 text-sm">
        {notice ? (
          <p
            data-testid="shortfall-notice"
            className="rounded-sm border border-accent/40 bg-accent-soft/30 px-3 py-2 text-xs text-fg"
          >
            {notice}
            {noticeLeaveId && teamId ? (
              <>
                {" · "}
                {/* The rest day is an ordinary lead-written leave row. The SPA
                    has no per-leave detail page for a lead (only the member's
                    own `/me/requests/:leaveId`), and the roster's Leaves tab is
                    GONE — #730 moved leave editing to the Team page, so
                    `/cycles/:id/leaves` rendered the workspace with no such
                    surface (backlog #137, checklist 06.14). This points at the
                    live list, and closes the modal on the way so the lead is
                    not left with a panel floating over a page they left. */}
                <Link
                  data-testid="shortfall-notice-leave-link"
                  to={`/teams/${teamId}`}
                  onClick={() => onOpenChange(false)}
                  className="underline underline-offset-2"
                >
                  recorded as leave
                </Link>
              </>
            ) : null}
          </p>
        ) : null}
        {restOffers.map((offer) => {
          const offerKey = offer.date ? `rest:${offer.membership_id}:${offer.date}` : null;
          const offerPending = offerKey !== null && pending?.key === offerKey ? pending : null;
          return (
            <section
              key={`${offer.membership_id}:${offer.date ?? "none"}`}
              data-testid="shortfall-rest-offer"
              className="space-y-2 rounded-sm border border-border p-3"
            >
              <p className="text-[11px] font-semibold uppercase tracking-wide text-fg-faint">
                {offer.kind === "call" || offer.kind === "call_later"
                  ? "Rest after the call"
                  : "Rest after the recall"}
              </p>
              <p data-testid="shortfall-rest-note" className="text-xs text-fg">
                {offer.note}
              </p>
              {/* A recall's day back is offered only when it opens no gap; the
                  post-call day of an activated call is THE day after the call
                  (#2211), offered either way — its note names any gap. */}
              {(offer.safe || offer.kind === "call") && offer.date ? (
                offerPending ? (
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="text-[11px] text-fg">{offerPending.question}</p>
                    <div className="flex flex-wrap gap-1.5">
                      <Button
                        type="button"
                        size="sm"
                        className="min-h-tap sm:min-h-0"
                        disabled={busy}
                        onClick={() => void confirmPending()}
                        data-testid={`shortfall-rest-confirm-${offer.membership_id}`}
                      >
                        {busy ? "Saving…" : "Yes, confirm"}
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        className="min-h-tap sm:min-h-0"
                        disabled={busy}
                        onClick={() => setPending(null)}
                      >
                        Not now
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-1.5">
                    <Button
                      data-testid="shortfall-rest-give"
                      type="button"
                      size="sm"
                      variant="outline"
                      className="min-h-tap sm:min-h-0"
                      disabled={busy}
                      onClick={() => askRest(offer)}
                    >
                      {offer.kind === "call" || offer.kind === "call_later"
                        ? `Give ${restDayLabel(offer.date)} off`
                        : `Give ${shortDate(offer.date)} back`}
                    </Button>
                    {offer.can_keep ? (
                      <Button
                        data-testid="shortfall-rest-keep"
                        type="button"
                        size="sm"
                        variant="ghost"
                        className="min-h-tap sm:min-h-0"
                        disabled={busy}
                        onClick={() => void doKeep(offer)}
                      >
                        {keepLabel(offer.released)}
                      </Button>
                    ) : null}
                  </div>
                )
              ) : null}
            </section>
          );
        })}
        {writeError ? (
          <p data-testid="shortfall-error" role="alert" className="text-xs text-danger">
            {writeError}
          </p>
        ) : null}
        {/* #2263 — a held re-solve refused the cover: one press keeps the
            roster that is out, then the lead tries again. */}
        <HeldResolveDiscard cycleId={cycleId} error={writeError} />
        {loadError ? (
          <p role="alert" className="text-xs text-danger">
            {loadError}
          </p>
        ) : null}
        {optionsQuery.isLoading ? (
          <p className="text-xs text-fg-subtle">Reading this day…</p>
        ) : null}
        {!optionsQuery.isLoading && !loadError && cells.length === 0 ? (
          <p data-testid="shortfall-none" className="text-xs text-fg-subtle">
            Nothing is short on this day.
          </p>
        ) : null}

        {ordered.map((cell) => {
          const key = cellKey(cell);
          const allRecalls = cell.recall ?? [];
          const allCancels = cell.cancel_leave ?? [];
          const isOpen = expanded[key] ?? false;
          // Candidates the lead cannot pick are collapsed behind a count
          // line: on a big team a wall of dead Confirms buried the two
          // people who could actually be called.
          const recalls = isOpen ? allRecalls : allRecalls.filter((c) => !blockedReason(c));
          const cancels = isOpen ? allCancels : allCancels.filter((c) => !blockedReason(c));
          const blocked = [...allRecalls, ...allCancels].map(blockedReason);
          const notEligible = blocked.filter((r) => r === "not_eligible").length;
          const alreadyWorking = blocked.filter((r) => r === "already_working").length;
          const hiddenCount = notEligible + alreadyWorking;
          // #2211 — someone on standby that night (a short real call only).
          const standby = cell.standby ?? [];
          const hasRung = allRecalls.length > 0 || allCancels.length > 0 || standby.length > 0;
          const allFree = cell.free ?? [];
          // #1999 — a free person the "Open this seat to" prompt offers is
          // covering another grade's seat: listed THERE only, so nobody is
          // offered twice behind two buttons that both read "Seat <name>".
          const free = allFree.filter((c) => !c.listed_in_open_to);
          const movedToOpen = allFree.length - free.length;
          const freeOpen = freeExpanded[key] ?? false;
          // A parked seat question keeps its row on screen even past the cut.
          const freeShown = free.filter(
            (c, i) =>
              freeOpen || i < FREE_SHOWN || pending?.key === `seat:${key}:${c.membership_id}`,
          );
          // Nobody on any rung the lead can pick — rung 0 counts: a free
          // person is seated directly rather than asked for.
          const moves = cell.move ?? [];
          const pickable =
            allFree.length > 0 ||
            moves.length > 0 ||
            standby.some((c) => c.eligible) ||
            [...allRecalls, ...allCancels].some((c) => blockedReason(c) === null);
          const askOutcome = askOutcomes[key] ?? null;
          const justAsked = askOutcome?.created ?? 0;
          // An ask sent from this panel counts before the re-read lands.
          const openAsks = Math.max(cell.open_cover_asks ?? 0, justAsked);
          // The seat(s) of someone off sick today behind this cell — only on a
          // cell with nobody to pick, nobody asked yet, and both components on.
          const askGroups =
            canAskTeam && date && !pickable && openAsks === 0 && askOutcome === null
              ? askGroupsFor(cell, mcToday, date)
              : [];
          return (
            <section
              key={key}
              data-testid={`shortfall-cell-${key}`}
              className="space-y-3 rounded-sm border border-border p-3"
            >
              <h3 className="text-sm font-medium text-fg">
                Short by {cell.shortfall}, {cellTitle(cell)}
                {openAsks > 0 ? (
                  // The same words the Review cover inbox uses: the team was
                  // already asked, so a second lead here does not ask again.
                  <>
                    {" "}
                    <span
                      data-testid={`shortfall-cover-asks-${key}`}
                      className="ml-1 rounded-sm border border-accent/40 bg-accent-soft/30 px-1 py-px align-middle text-[10px] font-normal text-fg"
                    >
                      Cover ask out · {openAsks}
                    </span>
                  </>
                ) : null}
              </h3>
              {cell.kind === "floor" ? (
                <p
                  data-testid={`shortfall-floor-line-${key}`}
                  className="text-[11px] text-fg-muted"
                >
                  One of {(cell.tiers ?? []).map((t) => t.toUpperCase()).join(" / ")} somewhere
                  across {(cell.stations ?? []).join(", ")} · a Confirm places them on{" "}
                  {cell.station}
                </p>
              ) : null}

              {free.length > 0 ? (
                <div className="space-y-1.5" data-testid={`shortfall-free-${key}`}>
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-fg-faint">
                    Seat someone free · {free.length}
                  </p>
                  {cell.free_note ? (
                    <p
                      data-testid={`shortfall-free-note-${key}`}
                      className="text-[11px] text-fg-subtle"
                    >
                      {cell.free_note}
                    </p>
                  ) : null}
                  {freeShown.map((candidate) => {
                    const rowKey = `seat:${key}:${candidate.membership_id}`;
                    return (
                      <CandidateRow
                        key={rowKey}
                        data-testid={`shortfall-free-row-${candidate.membership_id}-${key}`}
                        actionTestId={`shortfall-seat-${candidate.membership_id}-${key}`}
                        confirmTestId={`shortfall-seat-confirm-${candidate.membership_id}-${key}`}
                        name={candidate.display_name}
                        tier={candidate.tier}
                        line={freeLine(candidate)}
                        why={whyLines(candidate)}
                        tags={[]}
                        busy={busy}
                        saving={false}
                        actionLabel={`Seat ${firstName(candidate.display_name)}`}
                        pending={pending?.key === rowKey ? pending : null}
                        onAsk={() => askSeat(cell, candidate)}
                        onConfirm={() => void confirmPending()}
                        onDismiss={() => setPending(null)}
                      />
                    );
                  })}
                  {free.length > FREE_SHOWN ? (
                    <p className="text-[11px] text-fg-subtle">
                      {freeOpen
                        ? `All ${free.length} free shown`
                        : `${free.length - freeShown.length} more free`}
                      {" · "}
                      <button
                        type="button"
                        data-testid={`shortfall-free-toggle-${key}`}
                        className="inline-flex min-h-tap items-center underline underline-offset-2 sm:min-h-0"
                        onClick={() => setFreeExpanded((prev) => ({ ...prev, [key]: !freeOpen }))}
                      >
                        {freeOpen ? "Show fewer" : "Show all"}
                      </button>
                    </p>
                  ) : null}
                </div>
              ) : null}

              <MoveHereRung
                cellKey={key}
                candidates={moves}
                expanded={moveExpanded[key] ?? false}
                onToggle={() => setMoveExpanded((prev) => ({ ...prev, [key]: !prev[key] }))}
                pendingId={
                  pending?.key.startsWith(`move:${key}:`)
                    ? (pending.key.split(":").pop() ?? null)
                    : null
                }
                renderRow={(candidate) => {
                  const rowKey = `move:${key}:${candidate.membership_id}`;
                  return (
                    <CandidateRow
                      key={rowKey}
                      data-testid={`shortfall-move-row-${candidate.membership_id}-${key}`}
                      actionTestId={`shortfall-move-btn-${candidate.membership_id}-${key}`}
                      confirmTestId={`shortfall-move-confirm-${candidate.membership_id}-${key}`}
                      name={candidate.display_name}
                      tier={candidate.tier}
                      line={moveLine(candidate)}
                      why={whyLines(candidate)}
                      tags={[]}
                      busy={busy}
                      saving={false}
                      actionLabel="Move here"
                      pending={pending?.key === rowKey ? pending : null}
                      onAsk={() => askMove(cell, candidate)}
                      onConfirm={() => void confirmPending()}
                      onDismiss={() => setPending(null)}
                    />
                  );
                }}
              />

              {cell.open_to && ((cell.open_to.groups ?? []).length > 0 || cell.open_to.note)
                ? (() => {
                    // #1962 — nobody of the seat's own tier is free: open it.
                    const openTo = cell.open_to;
                    const groups = openTo.groups ?? [];
                    // #1999 — when rung 0's free people were moved here, the
                    // prompt opens on its first group with anyone free, so
                    // they stay in sight without a click (flows finding 27).
                    const firstFree = groups.find((g) =>
                      (g.people ?? []).some((p) => !p.not_free_reason),
                    );
                    const defaultChip =
                      movedToOpen > 0 && firstFree ? openGroupKey(firstFree) : null;
                    const chipKey = key in openChip ? (openChip[key] ?? null) : defaultChip;
                    const group = groups.find((g) => openGroupKey(g) === chipKey) ?? null;
                    const people = group?.people ?? [];
                    const allOpen = openAll[key] ?? false;
                    const shown = people.filter(
                      (p, i) =>
                        allOpen ||
                        i < FREE_SHOWN ||
                        pending?.key === `seat-open:${key}:${p.membership_id}`,
                    );
                    return (
                      <div className="space-y-1.5" data-testid={`shortfall-open-${key}`}>
                        <p className="text-[11px] text-fg">
                          Nobody in {openTo.tier_label} is free.
                          {groups.length > 0 ? " Open this seat to:" : ""}
                        </p>
                        {groups.length === 0 && openTo.note ? (
                          // #1999 — an empty Cover from: nobody may act up
                          // into the ward. One quiet line, where to set it.
                          <p
                            data-testid={`shortfall-open-note-${key}`}
                            className="text-[11px] text-fg-subtle"
                          >
                            {openTo.note}
                          </p>
                        ) : null}
                        <div className="flex flex-wrap gap-1.5">
                          {groups.map((g) => {
                            const gk = openGroupKey(g);
                            const isOpen = gk === chipKey;
                            const freeCount = (g.people ?? []).filter(
                              (p) => !p.not_free_reason,
                            ).length;
                            return (
                              <button
                                key={gk}
                                type="button"
                                data-testid={`shortfall-open-chip-${gk}-${key}`}
                                aria-pressed={isOpen}
                                className={cn(
                                  "inline-flex min-h-tap items-center rounded-sm border px-2 py-0.5 text-[11px] sm:min-h-0",
                                  isOpen
                                    ? "border-accent bg-accent-soft/30 text-fg"
                                    : "border-border text-fg",
                                )}
                                onClick={() => {
                                  setOpenChip((prev) => ({ ...prev, [key]: isOpen ? null : gk }));
                                  setOpenAll((prev) => ({ ...prev, [key]: false }));
                                }}
                              >
                                {g.label} · {freeCount}
                              </button>
                            );
                          })}
                        </div>
                        {group && people.length === 0 ? (
                          <p className="text-[11px] text-fg-subtle">Nobody in {group.label}.</p>
                        ) : null}
                        {shown.map((person) => {
                          const rowKey = `seat-open:${key}:${person.membership_id}`;
                          // #1964 — not free only because they sit on another
                          // ward that session: the seat moves them, and the
                          // knock-on is reviewed before anything is written.
                          const moves = knockOn.canMove(person);
                          return (
                            <CandidateRow
                              key={rowKey}
                              data-testid={`shortfall-open-row-${person.membership_id}-${key}`}
                              actionTestId={`shortfall-open-seat-${person.membership_id}-${key}`}
                              confirmTestId={`shortfall-open-seat-confirm-${person.membership_id}-${key}`}
                              name={person.display_name}
                              tier={person.tier_label}
                              line={openLine(person)}
                              why={person.not_free_reason ? [person.not_free_reason] : []}
                              tags={[]}
                              busy={busy || knockOn.loadingKey !== null}
                              blocked={Boolean(person.not_free_reason) && !moves}
                              blockedText={person.not_free_reason ?? undefined}
                              saving={knockOn.loadingKey === rowKey}
                              actionLabel={`Seat ${firstName(person.display_name)}${
                                moves ? " · moves them" : ""
                              }`}
                              pending={pending?.key === rowKey ? pending : null}
                              onAsk={() => {
                                if (!moves || !date) {
                                  askSeat(cell, person, true);
                                  return;
                                }
                                setWriteError(null);
                                setPending(null);
                                knockOn
                                  .launch(
                                    rowKey,
                                    {
                                      date,
                                      station: cell.station,
                                      session: cell.session,
                                      membershipId: person.membership_id,
                                      actingUp: true,
                                    },
                                    person.display_name,
                                  )
                                  .catch((err: unknown) => setWriteError(shortfallErrorText(err)));
                              }}
                              onConfirm={() => void confirmPending()}
                              onDismiss={() => setPending(null)}
                            />
                          );
                        })}
                        {people.length > FREE_SHOWN ? (
                          <p className="text-[11px] text-fg-subtle">
                            {allOpen
                              ? `All ${people.length} shown`
                              : `${people.length - shown.length} more`}
                            {" · "}
                            <button
                              type="button"
                              data-testid={`shortfall-open-toggle-${key}`}
                              className="inline-flex min-h-tap items-center underline underline-offset-2 sm:min-h-0"
                              onClick={() => setOpenAll((prev) => ({ ...prev, [key]: !allOpen }))}
                            >
                              {allOpen ? "Show fewer" : "Show all"}
                            </button>
                          </p>
                        ) : null}
                      </div>
                    );
                  })()
                : null}

              {standby.length > 0 ? (
                <div className="space-y-1.5" data-testid={`shortfall-standby-${key}`}>
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-fg-faint">
                    Activate a standby · {standby.length}
                  </p>
                  <p className="text-[11px] text-fg-subtle">
                    Their standby becomes this call, and they get the day after it off.
                  </p>
                  {standby.map((candidate) => {
                    const rowKey = `standby:${key}:${candidate.membership_id}`;
                    return (
                      <CandidateRow
                        key={rowKey}
                        data-testid={`shortfall-standby-row-${candidate.membership_id}-${key}`}
                        actionTestId={`shortfall-standby-activate-${candidate.membership_id}-${key}`}
                        confirmTestId={`shortfall-standby-confirm-${candidate.membership_id}-${key}`}
                        name={candidate.display_name}
                        tier={candidate.tier}
                        line={standbyLine(candidate, standbyRestMode)}
                        tags={[candidate.eligible ? "eligible" : "not listed as eligible"]}
                        why={[
                          ...(candidate.not_eligible_reason ? [candidate.not_eligible_reason] : []),
                          // #2284 — their other calls inside the gap window.
                          ...(candidate.eligible
                            ? gapCallLines(candidate.display_name, candidate.gap_calls)
                            : []),
                        ]}
                        busy={busy}
                        blocked={!candidate.eligible}
                        blockedText={candidate.not_eligible_reason ?? undefined}
                        saving={false}
                        actionLabel={`Activate ${firstName(candidate.display_name)}`}
                        pending={pending?.key === rowKey ? pending : null}
                        onAsk={() => askActivate(cell, candidate)}
                        onConfirm={() => void confirmPending()}
                        onDismiss={() => setPending(null)}
                      />
                    );
                  })}
                </div>
              ) : null}

              {recalls.length > 0 ? (
                <div className="space-y-1.5">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-fg-faint">
                    Recall someone post-call
                  </p>
                  {recalls.map((candidate) => {
                    const rowKey = `recall:${key}:${candidate.membership_id}`;
                    return (
                      <CandidateRow
                        key={rowKey}
                        data-testid={`shortfall-recall-${candidate.membership_id}`}
                        // The same person can be offered in two cells of one
                        // day (55 · AM and 55 · PM), so the ids on buttons
                        // that render in every such cell carry the cell.
                        actionTestId={`shortfall-recall-confirm-${candidate.membership_id}-${key}`}
                        costToggleTestId={`shortfall-recall-${candidate.membership_id}-cost-toggle-${key}`}
                        name={candidate.display_name}
                        tier={candidate.tier}
                        line={recallLine(candidate)}
                        // Already seated this day (recalled to the AM, say):
                        // they can still cover this cell, but the row must not
                        // read like an untouched rest.
                        note={
                          (candidate.called_back_to ?? []).length > 0
                            ? `Already called back on ${shortDate(date)} · now on ${(
                                candidate.called_back_to ?? []
                              ).join(", ")} · no second rest day owed`
                            : undefined
                        }
                        cost={candidate.cost}
                        // The rest rule in two words, with the sentence
                        // behind the tooltip + the "i" toggle.
                        badge={candidate.rest_hard ? "hard rest" : "soft rest"}
                        badgeTone={candidate.rest_hard ? "hard" : "soft"}
                        collapseCost
                        tags={[
                          candidate.eligible ? "eligible" : "not listed as eligible",
                          ...(candidate.already_working ? ["already working this session"] : []),
                        ]}
                        why={whyLines(candidate)}
                        busy={busy}
                        blocked={blockedReason(candidate) !== null}
                        blockedText={blockedText(candidate)}
                        saving={savingKey === rowKey}
                        // One click writes — no parked question on this rung.
                        actionLabel={`Recall ${firstName(candidate.display_name)}`}
                        pending={null}
                        onAsk={() => void doRecall(cell, candidate)}
                        onConfirm={() => void confirmPending()}
                        onDismiss={() => setPending(null)}
                      />
                    );
                  })}
                </div>
              ) : null}

              {cancels.length > 0 ? (
                <div className="space-y-1.5">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-fg-faint">
                    Cancel a leave
                  </p>
                  {cancels.map((candidate) => {
                    const rowKey = `cancel:${key}:${candidate.leave_id}`;
                    return (
                      <CandidateRow
                        key={rowKey}
                        data-testid={`shortfall-cancel-leave-${candidate.leave_id}`}
                        actionTestId={`shortfall-cancel-ask-${candidate.leave_id}-${key}`}
                        // One question is pending at a time, so this one is
                        // unique without the cell.
                        confirmTestId={`shortfall-cancel-confirm-${candidate.leave_id}`}
                        name={candidate.display_name}
                        tier={candidate.tier}
                        line={cancelLeaveLine(candidate)}
                        cost={candidate.cost}
                        tags={[candidate.eligible ? "eligible" : "not listed as eligible"]}
                        why={whyLines(candidate)}
                        busy={busy}
                        blocked={blockedReason(candidate) !== null}
                        blockedText={blockedText(candidate)}
                        saving={false}
                        actionLabel="Confirm"
                        pending={pending?.key === rowKey ? pending : null}
                        onAsk={() => askCancelLeave(cell, candidate)}
                        onConfirm={() => void confirmPending()}
                        onDismiss={() => setPending(null)}
                      />
                    );
                  })}
                </div>
              ) : null}

              {hiddenCount > 0 ? (
                <p className="text-[11px] text-fg-subtle">
                  {hiddenCountLine(notEligible, alreadyWorking)}
                  {" · "}
                  <button
                    type="button"
                    data-testid={`shortfall-hidden-toggle-${key}`}
                    className="inline-flex min-h-tap items-center underline underline-offset-2 sm:min-h-0"
                    onClick={() => setExpanded((prev) => ({ ...prev, [key]: !isOpen }))}
                  >
                    {isOpen ? "Hide" : "Show"}
                  </button>
                </p>
              ) : null}

              {/* Why THIS day's cell has the rungs it has. The cell's own
                  sentence names which rung came up empty; the payload-level
                  one is only a fallback for a response that predates it. */}
              {!hasRung ? (
                <p data-testid={`shortfall-empty-note-${key}`} className="text-xs text-fg-subtle">
                  {cell.empty_note || emptyNote}
                </p>
              ) : cell.empty_note ? (
                <p
                  data-testid={`shortfall-rung-note-${key}`}
                  className="text-[11px] text-fg-subtle"
                >
                  {cell.empty_note}
                </p>
              ) : null}
              {/* Ask the team, under the notes: the seat behind this empty
                  cell is someone off sick today, so the swap substrate can
                  find cover the rungs could not. One click, then the button
                  turns into the cover-ask line in place. */}
              {justAsked > 0 ? (
                <p
                  data-testid={`shortfall-cover-asks-line-${key}`}
                  className="text-[11px] text-fg-muted"
                >
                  Cover ask out · {justAsked} · the first teammate to accept covers it
                </p>
              ) : askOutcome ? (
                <p
                  data-testid={`shortfall-ask-team-none-${key}`}
                  className="text-[11px] text-fg-subtle"
                >
                  {askNoneText(askOutcome.skipped)}
                </p>
              ) : askGroups.length > 0 ? (
                <div className="space-y-1">
                  <Button
                    data-testid={`shortfall-ask-team-${key}`}
                    type="button"
                    size="sm"
                    variant="outline"
                    className="min-h-tap sm:min-h-0"
                    disabled={busy || askingKey !== null}
                    onClick={() => void askTeam(cell, askGroups)}
                  >
                    {askingKey === key
                      ? "Asking…"
                      : `Ask the team to cover ${askSeatWords(cell, askGroups)}`}
                  </Button>
                  {askFailed.includes(key) ? (
                    <p
                      data-testid={`shortfall-ask-team-error-${key}`}
                      role="alert"
                      className="text-[11px] text-danger"
                    >
                      Couldn't send the ask — try again.
                    </p>
                  ) : null}
                </div>
              ) : null}
            </section>
          );
        })}

        {mcToday.length > 0 ? (
          <section
            data-testid="shortfall-mc-footer"
            className="space-y-2 rounded-sm border border-border bg-bg-subtle p-3"
          >
            <p className="text-[11px] font-semibold uppercase tracking-wide text-fg-faint">
              {/* The ladder's day, named — it is rarely today (re-click r2-s3-13). */}
              Out sick on {shortDate(date)}
            </p>
            <ul className="space-y-0.5">
              {mcToday.map((item) => (
                <li
                  key={item.leave_id}
                  data-testid={`shortfall-mc-line-${item.membership_id}`}
                  className="text-xs text-fg"
                >
                  {date ? mcLine(item, date, cells) : item.display_name}
                </li>
              ))}
            </ul>
            {/* #1960 — re-solve the dates around the absence, fewest changes. */}
            {teamId && date ? (
              <FixAndResolveButton
                teamId={teamId}
                cycleId={cycleId}
                date={date}
                absent={mcToday.map((item) => ({
                  membership_id: item.membership_id,
                  display_name: item.display_name,
                  dates: item.dates,
                }))}
                onOpened={() => onOpenChange(false)}
                testid="shortfall-fix-resolve"
              />
            ) : null}
          </section>
        ) : null}
      </div>
    </CycleSurfaceModal>
  );
}

interface CandidateRowProps {
  "data-testid": string;
  /** Test id of the row's own button (`Recall Rae` / `Confirm`). */
  actionTestId?: string | undefined;
  /** Test id of the "Yes, confirm" a parked question renders. */
  confirmTestId?: string | undefined;
  /** Test id of the "i" toggle (`collapseCost` only) — unique per cell and
   *  candidate, where the row's own id repeats across cells. */
  costToggleTestId?: string | undefined;
  name: string;
  tier: string;
  /** The row's factual middle line (post-call of X, or the leave + approver). */
  line: string;
  /** A second line that changes how the row reads — a post-call person
   *  already called back this day. Omitted → nothing rendered. */
  note?: string | undefined;
  /** The backend's `cost` sentence — context, not a verdict. Omitted → no
   *  cost line (rung 0 costs nobody's rest or leave). */
  cost?: string;
  /** Why the row reads as it does — the not-eligible reason, a grade note. */
  why?: string[];
  /** Two words for the rule at stake (`hard rest`); the sentence stays in
   *  the tooltip and behind the "i". */
  badge?: string;
  badgeTone?: "hard" | "soft";
  /** Hide the `cost` sentence behind the "i" toggle instead of printing it.
   *  Phones have no hover, so the tooltip alone would lose it. */
  collapseCost?: boolean;
  tags: string[];
  busy: boolean;
  /** Listed for the record but not pickable (not eligible / already working). */
  blocked?: boolean;
  /** Why it is blocked: the disabled button's title and description. */
  blockedText?: string | undefined;
  /** THIS row's one-step write is in flight. */
  saving?: boolean;
  /** The button's words: `Recall Rae` writes at once; `Confirm` asks again. */
  actionLabel: string;
  /** Non-null while THIS row is the one question waiting to be answered. */
  pending: PendingConfirm | null;
  onAsk: () => void;
  onConfirm: () => void;
  onDismiss: () => void;
}

/** One person on one rung: who they are, why they're listed, one button.
 *
 *  The button is the whole difference between the rungs: a recall's says
 *  `Recall Rae` and writes on the click; a cancel-leave's says `Confirm`
 *  and parks the question for a second click. */
function CandidateRow({
  "data-testid": testid,
  actionTestId,
  confirmTestId,
  costToggleTestId,
  name,
  tier,
  line,
  note,
  cost,
  why = [],
  badge,
  badgeTone = "soft",
  collapseCost = false,
  tags,
  busy,
  blocked = false,
  blockedText,
  saving = false,
  actionLabel,
  pending,
  onAsk,
  onConfirm,
  onDismiss,
}: CandidateRowProps) {
  const [costOpen, setCostOpen] = useState(false);
  const blockedId = useId();
  return (
    <div
      data-testid={testid}
      className={cn(
        "flex flex-wrap items-start justify-between gap-2 rounded-sm border border-border px-2.5 py-2",
        pending ? "border-accent bg-accent-soft/20" : "bg-bg-subtle/30",
      )}
    >
      <div className="min-w-0 flex-1 basis-[14rem] space-y-0.5">
        <p className="text-xs font-medium text-fg">
          {name} <span className="font-normal text-fg-subtle">· {tier}</span>
          {badge ? (
            <span
              data-testid={`${testid}-badge`}
              title={cost}
              className={cn(
                "ml-1.5 rounded-sm border px-1 py-px align-middle text-[10px] font-normal leading-tight",
                badgeTone === "hard"
                  ? "border-danger/40 bg-danger/10 text-danger"
                  : "border-border text-fg-subtle",
              )}
            >
              {badge}
            </span>
          ) : null}
        </p>
        <p className="text-[11px] text-fg-muted">{line}</p>
        {note ? (
          <p data-testid={`${testid}-called-back`} className="text-[11px] font-medium text-fg">
            {note}
          </p>
        ) : null}
        {!cost ? null : collapseCost ? (
          <div className="space-y-0.5">
            <button
              type="button"
              data-testid={costToggleTestId}
              title={cost}
              aria-expanded={costOpen}
              aria-label={costOpen ? "Hide what this costs" : "What this costs"}
              // 20px glyph, 44px hit area on a phone (the padding, not the
              // circle, carries the tap target).
              className="flex min-h-tap min-w-tap items-center justify-start sm:min-h-0 sm:min-w-0"
              onClick={() => setCostOpen((v) => !v)}
            >
              <span className="flex h-5 w-5 items-center justify-center rounded-full border border-border text-[10px] leading-none text-fg-subtle hover:bg-bg-subtle">
                i
              </span>
            </button>
            {costOpen ? (
              <p data-testid={`${testid}-cost`} className="text-[11px] text-fg-faint">
                {cost}
              </p>
            ) : null}
          </div>
        ) : (
          <p data-testid={`${testid}-cost`} title={cost} className="text-[11px] text-fg-faint">
            {cost}
          </p>
        )}
        <div className="flex flex-wrap gap-1 pt-0.5">
          {tags.map((tag) => (
            <span
              key={tag}
              className="rounded-sm border border-border px-1 py-px text-[10px] leading-tight text-fg-subtle"
            >
              {tag}
            </span>
          ))}
        </div>
        {why.length > 0 ? (
          <div data-testid={`${testid}-why`} className="space-y-0.5">
            {why.map((line) => (
              <p key={line} className="text-[11px] text-fg-muted">
                {line}
              </p>
            ))}
          </div>
        ) : null}
        {blocked && blockedText ? (
          <span id={blockedId} className="sr-only">
            {blockedText}
          </span>
        ) : null}
      </div>
      {pending ? (
        <div className="flex w-full flex-col items-stretch gap-1.5 sm:w-auto sm:items-end">
          <p className="text-[11px] text-fg sm:max-w-xs sm:text-right">{pending.question}</p>
          <div className="flex flex-wrap gap-1.5 sm:justify-end">
            <Button
              type="button"
              size="sm"
              className="min-h-tap sm:min-h-0"
              disabled={busy}
              onClick={onConfirm}
              data-testid={confirmTestId}
            >
              {busy ? "Saving…" : (pending.confirmLabel ?? "Yes, confirm")}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="min-h-tap sm:min-h-0"
              disabled={busy}
              onClick={onDismiss}
            >
              Not now
            </Button>
          </div>
        </div>
      ) : (
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="min-h-tap sm:min-h-0"
          disabled={busy || blocked}
          onClick={onAsk}
          data-testid={actionTestId}
          title={blocked ? blockedText : undefined}
          aria-describedby={blocked && blockedText ? blockedId : undefined}
        >
          {saving ? "Saving…" : actionLabel}
        </Button>
      )}
    </div>
  );
}

export default ShortfallOptionsModal;
